import { useState } from "react";

// One star with a fillable left/right half. `fill` is 0, 0.5 or 1.
const Star = ({ fill, size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
    <defs><clipPath id={`half-${size}`}><rect x="0" y="0" width="12" height="24" /></clipPath></defs>
    <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z" fill="rgba(255,255,255,0.14)" stroke="rgba(251,191,36,0.55)" strokeWidth="1" />
    {fill > 0 && <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z" fill="#FBBF24" clipPath={fill === 0.5 ? `url(#half-${size})` : undefined} />}
  </svg>
);

export const Stars = ({ value = 0, size = 16, testId }) => (
  <span className="inline-flex items-center gap-0.5" role="img" aria-label={`${value} out of 5 stars`} data-testid={testId} data-rating={value}>
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} fill={value >= n ? 1 : value >= n - 0.5 ? 0.5 : 0} />)}
  </span>
);

// Tap the left half of a star for ½, the right half for a full star; keyboard arrows step by ½.
export const StarInput = ({ value, onChange, size = 40, testId = "star-input" }) => {
  const [hover, setHover] = useState(null);
  const shown = hover ?? value ?? 0;
  const pick = (e, n) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = (e.clientX ?? rect.left + rect.width) - rect.left < rect.width / 2;
    return left ? n - 0.5 : n;
  };
  const tap = (n) => onChange(value === n ? n - 0.5 : n); // touch: tap again to drop to ½
  const key = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); onChange(Math.min(5, (value || 0) + 0.5)); }
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); onChange(Math.max(0.5, (value || 0.5) - 0.5)); }
  };
  return (
    <div className="flex items-center gap-4 flex-wrap" data-testid={testId}>
      <div className="flex gap-1" role="slider" tabIndex={0} aria-valuemin={0.5} aria-valuemax={5} aria-valuenow={value || 0} aria-label="Star rating" onKeyDown={key} onMouseLeave={() => setHover(null)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" className="cursor-pointer transition-transform hover:scale-110 focus:outline-none" style={{ touchAction: "manipulation" }}
            onMouseMove={(e) => setHover(pick(e, n))} onClick={(e) => (e.detail === 0 || e.pointerType === "touch" ? tap(n) : onChange(pick(e, n)))}
            aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`${testId}-star-${n}`}>
            <Star size={size} fill={shown >= n ? 1 : shown >= n - 0.5 ? 0.5 : 0} />
          </button>
        ))}
      </div>
      <span className="font-display text-xl font-black tabular-nums text-amber-300" data-testid={`${testId}-value`}>{shown ? `${shown} / 5` : "Tap a star"}</span>
    </div>
  );
};
